import type { ChainedCommands } from '@tiptap/core';
import { findChildren, mergeAttributes } from '@tiptap/core';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import classNames from 'classnames';

import { Decoration_Type } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type { DOMOutputSpec } from './../../ricos-types';
import type { NamedLink } from './../../_shared/link/types';

import { createRicosMarkExtension } from '../create-ricos-extension';
import styles from '../statics/styles-scss';
import { ANCHOR_LINK } from './data-hooks';
import { cleanAndSetSelection, getSelectedMarkRangeByTypeNames } from './utils';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    anchor: {
      /**
       * Set an anchor mark
       */
      setAnchor: (data: NamedLink) => ReturnType;
      /**
       * Unset an anchor mark
       */
      unsetAnchor: () => ReturnType;
      /**
       * Scroll view to anchor
       */
      scrollToAnchor: (anchor: string) => ReturnType;
    };
  }
}

const scrollIntoViewWithOffset = (element: HTMLElement) => {
  element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
};

export const anchor = createRicosMarkExtension<{
  HTMLAttributes: Record<string, string>;
  inclusive: boolean;
}>({
  type: 'mark',
  groups: [],
  name: Decoration_Type.ANCHOR,
  createExtensionConfig() {
    return {
      name: this.name,
      keepOnSplit: false,

      priority: 1000,

      addOptions: () => ({
        inclusive: false,
        HTMLAttributes: {},
      }),

      inclusive() {
        return this.options.inclusive;
      },

      addAttributes() {
        return {
          anchor: {
            default: '',
          },
        };
      },

      renderHTML({ HTMLAttributes }) {
        const { link, linkInViewer } = styles;
        const classes = classNames(link, linkInViewer);
        const anchorAttributes = HTMLAttributes.anchor
          ? {
              'ricos-anchor': HTMLAttributes.anchor,
              href: `#${HTMLAttributes.anchor}`,
              target: '_self',
            }
          : {};

        return [
          'a',
          mergeAttributes(this.options.HTMLAttributes, {
            ...anchorAttributes,
            class: classes,
            'data-hook': ANCHOR_LINK,
          }),
          0,
        ] as DOMOutputSpec;
      },

      parseHTML() {
        return [
          {
            tag: `a[data-hook~=${ANCHOR_LINK}][ricos-anchor]`,
            getAttrs: (node: $TSFixMe) => {
              const anchor = node.getAttribute('ricos-anchor') ?? '';
              return { anchor };
            },
          },
        ];
      },

      addCommands() {
        return {
          setAnchor:
            (data: NamedLink) =>
            ({ commands, state, tr, chain }) => {
              cleanAndSetSelection(
                this.name,
                Decoration_Type.LINK,
                commands.unsetLink,
                state.schema,
                tr.selection,
                state.doc,
                commands,
                data.defaultName || ''
              );
              const setLinkChain = chain().setMark(this.name, { anchor: data.anchor });

              return ((setLinkChain.setUnderline as any)() as ChainedCommands).run();
            },

          unsetAnchor:
            () =>
            ({ tr, state, chain }) => {
              const { from, to } = getSelectedMarkRangeByTypeNames(
                [this.name],
                state.schema,
                tr.selection
              );

              const unsetLinkChain = chain().setTextSelection({ from, to }).unsetMark(this.name);

              return ((unsetLinkChain.unsetUnderline as any)() as ChainedCommands).run();
            },
          scrollToAnchor:
            anchor =>
            ({ editor, tr }) => {
              const { view } = editor;
              const foundNodes = findChildren(view.state.doc, node => {
                return node?.attrs?.id?.toString() === anchor;
              });

              if (foundNodes.length === 0) {
                return false;
              }

              const targetNode = foundNodes[0];
              let targetDom: Node | null = view.nodeDOM(targetNode.pos);

              if (targetDom && targetDom instanceof HTMLElement && !targetDom.scrollIntoView) {
                // might be text node
                targetDom = targetDom.parentElement;
              }

              if (targetDom && targetDom instanceof HTMLElement) {
                scrollIntoViewWithOffset(targetDom);

                // If we don't set selection link toolbar will be still open,
                // even though it might not be visible anymore.
                try {
                  if (targetNode.node.isText || targetNode.node.isTextblock) {
                    tr.setSelection(
                      TextSelection.between(
                        tr.doc.resolve(targetNode.pos),
                        tr.doc.resolve(targetNode.pos)
                      )
                    );
                  } else {
                    tr.setSelection(NodeSelection.create(tr.doc, targetNode.pos));
                  }
                } catch (e) {
                  // If for some reason selection position is not valid,
                  // PM will throw an error.
                  console.error('Failed to set selection on anchor', e);
                }

                return true;
              } else {
                return false;
              }
            },
        };
      },
    };
  },
});
